import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api';
import { identify as cdpIdentify } from '../utils/personTracker';
import useFeatureSync from '../hooks/useFeatureSync';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPrograms, setHasPrograms] = useState(false);
  const [features, setFeatures] = useState({}); // { feature_key: bool } (sistema legado)
  const [featuresAdmin, setFeaturesAdmin] = useState(false);
  // Feature Governance™ — flags são derivadas DIRETO do `user.feature_flags`,
  // que vem embutido no payload do login e do /me. Zero race condition,
  // zero fetch extra, zero cache issue: se o usuário está logado, os flags
  // já estão dentro do próprio objeto do usuário.

  useEffect(() => {
    // Feature Governance™ Migration — se o localStorage.user for de uma sessão
    // ANTIGA (antes de feature_flags existirem no payload), forçamos refresh
    // síncrono do /me ANTES do primeiro render popular o Navbar. Sem isso,
    // o Navbar renderiza com user sem feature_flags → flagOn cai no fallback
    // TRUE → itens desativados no admin ainda aparecem por 1 tick.
    const bootstrap = async () => {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) { setLoading(false); return; }
      let userData;
      try { userData = JSON.parse(storedUser); } catch { setLoading(false); return; }
      if (!userData?.user) { setLoading(false); return; }
      const token = userData.token || localStorage.getItem('token');
      if (token && !localStorage.getItem('token')) {
        localStorage.setItem('token', token);
      }
      const hasFlags = 'feature_flags' in userData.user;
      // Sessão antiga (pré-Feature Governance) → bloqueia até /me devolver flags frescos
      if (!hasFlags && token) {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: 'no-store',
          });
          if (res.status === 401) {
            // Token expirado → limpa sessão pra forçar re-login (evita servir stale)
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            setLoading(false);
            return;
          }
          if (res.ok) {
            const fresh = await res.json();
            const merged = { ...userData.user, ...fresh };
            setUser(merged);
            localStorage.setItem('user', JSON.stringify({ user: merged, token }));
            checkUserPrograms();
            loadFeatures();
            setLoading(false);
            return;
          }
        } catch { /* fall through */ }
      }
      // Sessão fresca (já com feature_flags) → fast path + refresh async
      setUser(userData.user);
      checkUserPrograms();
      loadFeatures();
      refreshUserFromMe();
      setLoading(false);
    };
    bootstrap();
  }, []);

  // ✨ Feature Sync™ — Real-Time Feature Governance (Fev/2026)
  // Detecta em ≤ 30s automaticamente qualquer mudança de features da empresa
  // e refaz o /me + toast sutil. Sem F5, sem apagar cookies, sem trocar de aba.
  // Poll leve, só quando aba visível, BroadcastChannel entre abas.
  useFeatureSync(
    async () => {
      const before = user?.feature_flags || {};
      await refreshUserFromMe();
      // Toast sutil (window-scoped, sem dependência de lib)
      try {
        const evt = new CustomEvent('fastconformity-toast', {
          detail: {
            title: 'Configurações atualizadas',
            message: 'Seu menu foi sincronizado com as últimas mudanças da empresa.',
            variant: 'sync',
          },
        });
        window.dispatchEvent(evt);
      } catch { /* ignore */ }
    },
    () => !!localStorage.getItem('token')
  );

  const refreshUserFromMe = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (res.status === 401) {
        // Token expirado → limpa sessão (evita servir feature_flags stale eternamente)
        try {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setUser(null);
          window.dispatchEvent(new Event('auth-change'));
        } catch { /* ignore */ }
        return;
      }
      if (!res.ok) return;
      const fresh = await res.json();
      setUser((prev) => {
        const merged = { ...(prev || {}), ...fresh };
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          localStorage.setItem('user', JSON.stringify({ ...stored, user: merged }));
        } catch { /* ignore */ }
        return merged;
      });
    } catch { /* ignore */ }
  };

  const loadFeatures = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/features/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFeatures(data.features || {});
        setFeaturesAdmin(!!data.is_admin);
      }
    } catch (_) { /* ignore */ }
  };

  const hasFeature = (key) => {
    if (featuresAdmin) return true;
    return !!features[key];
  };

  // Feature Governance™ — flags derivadas DIRETO do user.feature_flags.
  // Fonte única de verdade: o próprio objeto do usuário logado. Backend
  // já entrega os overrides no login e no /me.
  //   • sem user logado         → retorna true (rotas públicas)
  //   • user tem feature_flags  → false SÓ se explicitamente false (undefined = default ON)
  //   • user SEM feature_flags  → sessão stale/pré-Governance → hide por segurança
  //                                (o bootstrap async no useEffect já força refresh)
  const flagOn = (code) => {
    if (!user) return true; // rotas públicas / logged-out
    const flags = user.feature_flags;
    if (flags === undefined || flags === null) return false; // stale session — safer to hide
    return flags[code] !== false;
  };

  const checkUserPrograms = async () => {
    try {
      const { programAPI } = await import('../api');
      const response = await programAPI.hasPrograms();
      setHasPrograms(response.hasPrograms);
    } catch (error) {
      console.error('Error checking programs:', error);
      setHasPrograms(false);
    }
  };

  const login = async (email, password, mfaToken = null, captchaToken = null, trustDevice = false) => {
    try {
      const deviceKey = `mfa_device_token:${(email || '').toLowerCase()}`;
      let storedDeviceToken = null;
      try { storedDeviceToken = localStorage.getItem(deviceKey); } catch { /* ignore */ }

      const response = await authAPI.loginWithMFA(email, password, mfaToken, captchaToken, storedDeviceToken, trustDevice);

      // Persist a newly-issued trusted-device token (skip MFA next time on this browser)
      if (response.device_token) {
        try { localStorage.setItem(deviceKey, response.device_token); } catch { /* ignore */ }
      }

      // Check if MFA is required
      if (response.mfa_required) {
        return { 
          success: false, 
          mfa_required: true,
          user_id: response.user_id,
          message: response.message 
        };
      }
      
      // Backend returns {token, user} directly, not {success: true, ...}
      if (response.token && response.user) {
        const userData = { user: response.user, token: response.token };
        setUser(response.user);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', response.token); // Also store token separately for compatibility
        // CDP · identify — amarra visitor_id anônimo à identidade agora conhecida
        try {
          cdpIdentify({
            email: response.user.email,
            user_id: response.user.id || response.user.user_id,
            display_name: response.user.name,
            company: response.user.company_name,
            role: response.user.role,
          });
        } catch { /* silencioso */ }
        try { window.dispatchEvent(new Event('auth-change')); } catch { /* ignore */ }
        checkUserPrograms(); // Check if user has programs
        loadFeatures(); // Carrega feature flags do usuário (sistema legado)
        return { success: true, user: response.user };
      }
      return { success: false, error: 'Login failed' };
    } catch (error) {
      // Backend usa HTTP 428 com detail.captcha_required=true quando exige puzzle
      const detail = error.response?.data?.detail;
      const captchaRequired = error.response?.status === 428
        || (typeof detail === 'object' && detail?.captcha_required);
      return { 
        success: false, 
        captcha_required: captchaRequired || false,
        error: (typeof detail === 'string' ? detail : detail?.message) || 'Invalid credentials',
        mfa_invalid: error.response?.data?.mfa_invalid 
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await authAPI.register(name, email, password);
      if (response.success) {
        const userData = { user: response.user, token: response.token };
        setUser(response.user);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', response.token); // Also store token separately for compatibility
        // CDP · identify
        try {
          cdpIdentify({
            email: response.user.email,
            user_id: response.user.id || response.user.user_id,
            display_name: response.user.name,
            company: response.user.company_name,
            role: response.user.role,
          });
        } catch { /* silencioso */ }
        try { window.dispatchEvent(new Event('auth-change')); } catch { /* ignore */ }
        return { success: true, user: response.user };
      }
      return { success: false, error: 'Registration failed' };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    setHasPrograms(false);
    setFeatures({});
    setFeaturesAdmin(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('videoProgress');
    try { window.dispatchEvent(new Event('auth-change')); } catch { /* ignore */ }
    try {
      // limpa caches antigos e novo (versionados)
      sessionStorage.removeItem('demoStatus_v2');
      sessionStorage.removeItem('demoStatus_v3');
    } catch { /* ignore */ }
  };

  // Detecta sessão de impersonação ativa via claim `imp` no JWT.
  // Quando ativo, `isAdmin` é forçado a false em TODO o app (sidebar admin,
  // navbar "Painel Admin", CommandPalette, etc) para refletir a visão do
  // usuário-alvo. Defesa em profundidade contra vazamento de UI privilegiada.
  const isImpersonating = (() => {
    try {
      const t = localStorage.getItem('token');
      if (!t) return false;
      const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return Boolean(payload?.imp);
    } catch { return false; }
  })();

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAdmin: user?.role === 'admin' && !isImpersonating,
    isKbManager: !!user?.kb_manager,
    isImpersonating,
    hasPrograms,
    features,
    hasFeature,
    reloadFeatures: loadFeatures,
    // Feature Governance™ — flagOn deriva de user.feature_flags (fonte única)
    flagOn,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
